/**
 * Compute waveform peaks for efficient canvas rendering
 * Input: AudioBuffer or Float32Array
 * Output: JSON with peaks ({ version, sampleRate, blockSize, peaks })
 */

export interface PeaksData {
  version: number;
  sampleRate: number;
  blockSize: number;
  peaks: number[]; // [min0, max0, min1, max1, ...]
}

export class PeaksComputer {
  static computeFromBuffer(audioBuffer: AudioBuffer, blockSize: number = 4096): PeaksData {
    const { sampleRate, numberOfChannels, length } = audioBuffer;
    const channelData = audioBuffer.getChannelData(0); // Use first channel

    return PeaksComputer.computeFromFloat32(
      channelData,
      sampleRate,
      blockSize
    );
  }

  static computeFromFloat32(
    pcmFloat32: Float32Array,
    sampleRate: number,
    blockSize: number = 4096
  ): PeaksData {
    const numBlocks = Math.ceil(pcmFloat32.length / blockSize);
    const peaks: number[] = [];

    for (let i = 0; i < numBlocks; i++) {
      const start = i * blockSize;
      const end = Math.min(start + blockSize, pcmFloat32.length);
      const block = pcmFloat32.subarray(start, end);

      let min = 1;
      let max = -1;

      for (let j = 0; j < block.length; j++) {
        const sample = block[j];
        if (sample < min) min = sample;
        if (sample > max) max = sample;
      }

      peaks.push(min, max);
    }

    return {
      version: 1,
      sampleRate,
      blockSize,
      peaks,
    };
  }

  static toJSON(peaksData: PeaksData): string {
    return JSON.stringify(peaksData);
  }

  static fromJSON(json: string): PeaksData {
    return JSON.parse(json);
  }
}
